import http from '@monolith/legacy/services/api/http';
import type { GermanBiSubmitPayload, RegistryAddress, RegistrySearchResult } from '../types/german-bi.types';

interface BusinessRegistryAddress {
  street1?: string;
  street2?: string;
  zipCode?: string;
  city: string;
  countryCode?: string;
  siege?: boolean;
}

interface BusinessRegistryResponse {
  data: {
    type: string;
    id: string;
    attributes: {
      name: string;
      registryOffice: string | null;
      addresses: BusinessRegistryAddress[];
    };
  };
}

function extractSiegeAddress(addresses: BusinessRegistryAddress[]): RegistryAddress | undefined {
  const siege = addresses.find((a) => a.siege);
  if (!siege) {
    return undefined;
  }

  const street = [siege.street1, siege.street2].filter(Boolean).join(' ');
  if (!street && !siege.city && !siege.zipCode) {
    return undefined;
  }

  return {
    street: street || '',
    city: siege.city || '',
    postalCode: siege.zipCode || '',
    countryCode: siege.countryCode || '',
  };
}

export async function searchRegistry(query: string): Promise<RegistrySearchResult[]> {
  if (!query || query.length < 3) {
    return [];
  }

  try {
    const response = await http().get<BusinessRegistryResponse>('/api/business-registry/v1/business', {
      params: {
        identifier: query,
        country: 'DE',
      },
    });

    const business = response.data.data;
    const city = business.attributes.addresses?.[0]?.city || '';
    const hqAddress = extractSiegeAddress(business.attributes.addresses || []);

    const result: RegistrySearchResult = {
      id: business.id,
      companyName: business.attributes.name,
      registryNumber: business.id,
      registryCourt: business.attributes.registryOffice || city,
    };

    if (hqAddress) {
      result.hqAddress = hqAddress;
    }

    return [result];
  } catch {
    return [];
  }
}

export async function submitGermanBi(payload: GermanBiSubmitPayload): Promise<{ success: boolean }> {
  const { type, registryNumber, soleTraderNumber, hqAddress } = payload;

  if (type === 'has_handelsregister' && (registryNumber || hqAddress)) {
    const body: Record<string, unknown> = {};

    if (registryNumber) {
      body.tax_number = registryNumber.replace(/\s/g, '').toUpperCase();
    }

    if (hqAddress) {
      body.hq_address = {
        street: hqAddress.street,
        city: hqAddress.city,
        postal_code: hqAddress.postalCode,
        country_id: hqAddress.countryId,
        country_iso_code: hqAddress.countryIsoCode,
      };
    }

    await http().patch('/api/me/tax-number', body);
  } else if (type === 'sole_trader') {
    await http().patch('/api/me/sole-trader-number', {
      sole_trader_number: soleTraderNumber || '',
    });
  }

  return { success: true };
}
